import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import Breadcrumb from 'components/ui/Breadcrumb';
import Icon from 'components/AppIcon';

import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ProfileForm from './components/ProfileForm';

export default function EditableProfileScreen() {
  const navigate = useNavigate();
  const { user, loading, isAuthenticated, signOut } = useAuth();

  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedName, setSavedName] = useState('');

  // Auth guard
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login-screen', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  // Load profile data from volunteers table + auth metadata
  useEffect(() => {
    async function loadProfile() {
      if (!user) return;
      setProfileLoading(true);
      try {
        // Try volunteers table first
        const { data: volunteerData } = await supabase
          ?.from('volunteers')
          ?.select('name, email, mobile, school, dob')
          ?.eq('user_id', user?.id)
          ?.single();

        if (volunteerData) {
          setProfileData(volunteerData);
        } else {
          // Fall back to auth metadata
          setProfileData({
            name: user?.user_metadata?.full_name || user?.user_metadata?.name || '',
            email: user?.email || '',
            mobile: user?.user_metadata?.mobile || '',
            school: user?.user_metadata?.school || '',
            dob: user?.user_metadata?.dob || '',
          });
        }
      } catch (err) {
        // Fall back to auth metadata on error
        setProfileData({
          name: user?.user_metadata?.full_name || user?.user_metadata?.name || '',
          email: user?.email || '',
          mobile: user?.user_metadata?.mobile || '',
          school: user?.user_metadata?.school || '',
          dob: user?.user_metadata?.dob || '',
        });
      } finally {
        setProfileLoading(false);
      }
    }

    if (!loading && isAuthenticated) {
      loadProfile();
    }
  }, [user, loading, isAuthenticated]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login-screen');
  };

  function handleSaveSuccess(updatedData) {
    setSavedName(updatedData?.name || profileData?.name || 'Volunteer');
    setSaveSuccess(true);
    // Update local profile data
    setProfileData((prev) => ({ ...prev, ...updatedData }));
  }

  const volunteer = user ? {
    name: user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')?.[0] || 'Volunteer',
    email: user?.email,
    status: 'new',
  } : null;

  // Loading state
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-caption text-muted-foreground">Loading your profile…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header
        isAuthenticated={true}
        user={{ name: volunteer?.name, email: volunteer?.email, status: volunteer?.status }}
        onLogout={handleLogout}
      />

      <main className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb />
        </div>

        {/* Page Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/volunteer-dashboard')}
              className="w-9 h-9 rounded-lg border border-border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Back to dashboard"
            >
              <Icon name="ArrowLeft" size={18} />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">Edit Profile</h1>
              <p className="text-sm font-caption text-muted-foreground mt-0.5">Update your personal information</p>
            </div>
          </div>
        </div>

        {/* Success Banner */}
        {saveSuccess && (
          <div className="mb-6 p-4 bg-success/10 border border-success/30 rounded-xl flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-heading font-semibold text-foreground">
                Profile updated successfully!
              </p>
              <p className="text-xs font-caption text-muted-foreground mt-0.5">
                Your changes have been saved. Welcome, {savedName}!
              </p>
            </div>
            <button
              onClick={() => setSaveSuccess(false)}
              className="text-muted-foreground hover:text-foreground flex-shrink-0"
              aria-label="Dismiss"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-2xl">
          <div className="bg-card border border-border rounded-xl shadow-sm">
            {/* Card Header */}
            <div className="px-5 md:px-6 py-4 border-b border-border flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground font-heading font-bold text-base">
                  {profileData?.name ? profileData?.name?.charAt(0)?.toUpperCase() : 'V'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-heading font-semibold text-foreground truncate">
                  {profileData?.name || 'Volunteer'}
                </p>
                <p className="text-xs font-caption text-muted-foreground truncate">
                  {profileData?.email || ''}
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="px-5 md:px-6 py-6">
              {profileData && (
                <ProfileForm
                  initialData={profileData}
                  onSaveSuccess={handleSaveSuccess}
                />
              )}
            </div>
          </div>

          {/* Info note */}
          <div className="mt-4 flex items-start gap-2 text-xs font-caption text-muted-foreground">
            <Icon name="Info" size={13} className="mt-0.5 flex-shrink-0" />
            <span>
              Changing your email or mobile number requires verification via OTP.
              Your date of birth cannot be changed here — contact support if needed.
            </span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card mt-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
              <Icon name="Heart" size={13} color="white" />
            </div>
            <span className="text-sm font-heading font-semibold text-foreground">VolunteerHub</span>
          </div>
          <p className="text-xs font-caption text-muted-foreground text-center">
            &copy; {new Date()?.getFullYear()} VolunteerHub. All rights reserved.
          </p>
          <div className="flex items-center gap-1 text-xs font-caption text-muted-foreground">
            <Icon name="ShieldCheck" size={13} color="var(--color-success)" />
            SSL Secured
          </div>
        </div>
      </footer>
    </div>
  );
}
